import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Search } from 'lucide-react';
import { apiRequest } from '@/lib/queryClient';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

type GlobalSearchItem = {
  type: string;
  id: string;
  title: string;
  subtitle?: string;
  href: string;
};

type GlobalSearchResponse = {
  query: string;
  groups: Array<{
    entityType: string;
    label: string;
    sections: Array<{
      field: string;
      label: string;
      total: number;
      items: GlobalSearchItem[];
    }>;
  }>;
};

export default function GlobalCrmSearch({ isV2 = false }: { isV2?: boolean }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen((current) => !current);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(query.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [query]);

  const searchUrl = useMemo(
    () => `/api/crm-query/global-search?q=${encodeURIComponent(debouncedQuery)}&limitPerSection=5`,
    [debouncedQuery],
  );
  const { data, isFetching } = useQuery<GlobalSearchResponse>({
    queryKey: [searchUrl],
    queryFn: () => apiRequest(searchUrl),
    enabled: open && debouncedQuery.length >= 2,
    staleTime: 30_000,
  });

  const resultGroups = Array.isArray(data?.groups) ? data.groups : [];
  const hasResults = resultGroups.some((group) => group.sections.length > 0);
  return (
    <>
      <button
        type="button"
        data-ui="global-search-full"
        onClick={() => setOpen(true)}
        className={cn(
          'hidden h-9 min-w-0 flex-1 items-center gap-2 border border-border bg-background px-3 text-left text-sm text-muted-foreground hover:bg-accent md:flex',
          isV2 ? 'max-w-sm' : 'max-w-lg',
        )}
        aria-label="Apri la ricerca globale"
      >
        <Search className="h-4 w-4 shrink-0" />
        <span className="min-w-0 flex-1 truncate">Cerca in tutto il CRM…</span>
        <kbd className="border border-border bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">⌘K</kbd>
      </button>

      <button
        type="button"
        data-ui="global-search-compact"
        onClick={() => setOpen(true)}
        className="inline-flex h-10 w-10 items-center justify-center border border-border bg-background text-muted-foreground md:hidden"
        aria-label="Apri la ricerca globale"
      >
        <Search className="h-5 w-5" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-ui="global-search-dialog"
          className="w-[calc(100%-2rem)] max-h-[calc(100dvh-2rem)] max-w-2xl overflow-hidden rounded-xl border border-border bg-background p-0"
        >
          <DialogTitle className="sr-only">Ricerca globale CRM</DialogTitle>
          <DialogDescription className="sr-only">
            Cerca record in tutti i moduli e visualizza il campo che ha prodotto ogni risultato.
          </DialogDescription>
          <div className="flex max-h-[calc(100dvh-2rem)] min-h-0 flex-col">
            <div className="flex shrink-0 items-center border-b border-border px-3">
              <Search className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              <input
                type="search"
                aria-label="Cerca in tutto il CRM"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Cerca aziende, contatti, deal, città, settori, tag…"
                autoFocus
                className="h-11 w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
              />
            </div>
            <div
              className="min-h-0 flex-1 overflow-y-auto overscroll-contain"
              aria-live="polite"
              aria-busy={isFetching}
            >
              {query.trim().length < 2 && (
                <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                  Digita almeno 2 caratteri. I risultati saranno separati per modulo e campo trovato.
                </div>
              )}
              {query.trim().length >= 2 && isFetching && (
                <div className="px-4 py-8 text-center text-sm text-muted-foreground">Ricerca in corso…</div>
              )}
              {query.trim().length >= 2 && !isFetching && !hasResults && (
                <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                  Nessun risultato in tutto il CRM.
                </div>
              )}
              {!isFetching && resultGroups.map((group) =>
                group.sections.map((section) => {
                  const headingId = `global-search-${group.entityType}-${section.field}`;
                  return (
                    <section key={`${group.entityType}-${section.field}`} aria-labelledby={headingId} className="border-b border-border p-1">
                      <h3 id={headingId} className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
                        {group.label} · {section.label} ({section.total})
                      </h3>
                      <ul className="space-y-0.5">
                        {section.items.map((item) => (
                          <li key={`${group.entityType}-${section.field}-${item.id}`}>
                            <a
                              href={item.href}
                              className="flex w-full min-w-0 items-center gap-3 px-3 py-2.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:ring-2 focus-visible:ring-primary"
                              aria-label={`Apri ${item.title} in ${group.label}, corrispondenza ${section.label}`}
                            >
                              <span className="min-w-0 flex-1">
                                <span className="block truncate font-medium">{item.title}</span>
                                {item.subtitle && (
                                  <span className="block truncate text-xs text-muted-foreground">{item.subtitle}</span>
                                )}
                              </span>
                              <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                            </a>
                          </li>
                        ))}
                      </ul>
                      {section.total > section.items.length && (
                        <a
                          href={`/search?q=${encodeURIComponent(query.trim())}&entityType=${encodeURIComponent(group.entityType)}&field=${encodeURIComponent(section.field)}`}
                          className="flex items-center justify-between border-t border-border px-3 py-2 text-sm font-medium text-primary hover:bg-accent"
                        >
                          <span>Vedi tutti i {section.total} risultati</span>
                          <ArrowRight className="h-4 w-4" aria-hidden="true" />
                        </a>
                      )}
                    </section>
                  );
                }),
              )}
              {!isFetching && hasResults && (
                <div className="sticky bottom-0 border-t border-border bg-background p-3">
                  <a
                    href={`/search?q=${encodeURIComponent(query.trim())}`}
                    className="flex items-center justify-center gap-2 border border-primary bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground"
                  >
                    Apri tutti i risultati in una pagina
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </a>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
